import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Tabs, Tab, Divider, Paper, Button, FormControl, Select, MenuItem, Tooltip, IconButton, TextField, Stack, Checkbox, Alert, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import SortIcon from '@mui/icons-material/Sort';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import ImagePreview from './ImagePreview';
import ImageAnnotationModal from './ImageAnnotationModal';
import apiService from '../services/api';
import { renameElevation } from '../services/api';
import { hasMeaningfulDefects } from '../utils/defects';
import { summarizeUploadSession } from '../utils/persistentUploads';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const isSection12SitePlan = (plan) => {
  const name = String(plan?.name || '').trim().toLowerCase();
  const description = String(plan?.description || '').trim().toLowerCase();
  const isElevationView = name.startsWith('elevation view -');
  const isFacade = name.startsWith('facade') || description.includes('facade');
  const isTapping = name.startsWith('tapping inspection -') || description.includes('tapping') || description.includes('close-range');
  return !isElevationView && !isFacade && !isTapping;
};

const FolderTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  minHeight: '48px',
}));

const FolderContainer = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

const FolderHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const FolderName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const ImageGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SelectableImageCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  outline: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  outlineOffset: 2,
}));

const FolderView = ({
  folderStructure,
  selectedFolder,
  setSelectedFolder,
  projectId = null,
  buildingId = null,
  buildingName = null,
  onUpdateFolderStructure,
  imageType = 'drone',
  uploadSession = null,
  onResumeUpload = null,
  previewOnly = false,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [elevationOrder, setElevationOrder] = useState([]);
  const [elevationCoverMap, setElevationCoverMap] = useState({});
  const [keyFindingsMap, setKeyFindingsMap] = useState(() => {
    try {
      const raw = localStorage.getItem(`key_findings_${imageType}_building_${buildingId || 'global'}`);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });
  const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
  const [selectedImageForAnnotation, setSelectedImageForAnnotation] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Handle tab change
  const handleChange = (event, newValue) => {
    setSelectedFolder(newValue);
  };
  
  const storageKey = React.useMemo(() => {
    if (buildingId) return `folder_renames_building_${buildingId}`;
    if (projectId) return `folder_renames_project_${projectId}`;
    return 'folder_renames';
  }, [buildingId, projectId]);

  const resolveElevationStorageName = useCallback((elevationName) => {
    const images = folderStructure?.[elevationName];
    const firstImage = Array.isArray(images) ? images[0] : null;
    return (
      firstImage?.sourceElevationName ||
      firstImage?.originalElevationName ||
      firstImage?.originalImage?.elevation_name ||
      elevationName
    );
  }, [folderStructure]);
  
  const beginRename = () => {
    if (!selectedFolder) return;
    setRenameValue(selectedFolder);
    setIsRenaming(true);
  };
  
  const cancelRename = () => {
    setIsRenaming(false);
    setRenameValue('');
  };
  
  const applyRename = async () => {
    const oldName = selectedFolder;
    const newName = (renameValue || '').trim();
    if (!oldName || !newName || oldName === newName) {
      setIsRenaming(false);
      return;
    }
    if (folderStructure[newName]) {
      alert('A folder with this name already exists.');
      return;
    }

    const storageOldName = resolveElevationStorageName(oldName);
    const images = folderStructure[oldName] || [];
    const imageIds = images.map((img) => String(img.id || '')).filter(Boolean);
    try {
      if (projectId || buildingId) {
        await renameElevation(storageOldName, newName, projectId, buildingId, imageType, imageIds);
      }
    } catch (err) {
      setError(err?.message || 'Failed to rename elevation');
      return;
    }

    const updated = { ...folderStructure };
    delete updated[oldName];
    updated[newName] = images.map(img => ({
      ...img,
      elevation: newName,
      folderName: newName,
      folderPath: newName,
      sourceElevationName: newName,
      originalElevationName: newName,
      originalImage: img.originalImage ? { ...img.originalImage, elevation_name: newName } : img.originalImage,
    }));
    onUpdateFolderStructure && onUpdateFolderStructure(updated);
    setSelectedFolder(newName);
    setElevationOrder((prev) => prev.map((name) => (name === oldName || name === storageOldName ? newName : name)));
    setIsRenaming(false);
    
    // Persist rename map to localStorage to survive reloads
    try {
      const raw = localStorage.getItem(storageKey);
      const map = raw ? JSON.parse(raw) : {};
      map[oldName] = newName;
      map[storageOldName] = newName;
      localStorage.setItem(storageKey, JSON.stringify(map));
    } catch {}
    
    // Update elevation_site_plan mapping on backend if needed
    try {
      if (buildingId) {
        const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
        const newMap = { ...(current?.elevation_map || {}) };
        const sitePlanKey = newMap[oldName] ? oldName : (newMap[storageOldName] ? storageOldName : null);
        if (sitePlanKey) {
          newMap[newName] = newMap[sitePlanKey];
          delete newMap[sitePlanKey];
          delete newMap[oldName];
          const payload = { ...current, elevation_map: newMap };
          await apiService.saveSitePlanAnnotations(buildingId, payload);
          setElevationSitePlanMap(newMap);
        } else if (newMap[newName]) {
          setElevationSitePlanMap(newMap);
        }
      }
    } catch (e) {
      console.error('Failed to persist elevation map rename:', e);
    }
  };

  const persistElevationOrder = useCallback(async (nextOrder) => {
    setElevationOrder(nextOrder);
    if (!buildingId) return;
    try {
      await apiService.saveElevationOrder(buildingId, nextOrder, imageType);
    } catch (e) {
      setError(e?.message || 'Failed to save elevation order');
    }
  }, [buildingId, imageType]);

  const moveElevationBy = useCallback(async (delta) => {
    const keys = elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure || {});
    if (!selectedFolder || keys.length <= 1) return;
    const idx = keys.indexOf(selectedFolder);
    if (idx < 0) return;
    const to = Math.max(0, Math.min(keys.length - 1, idx + delta));
    if (to === idx) return;
    const next = [...keys];
    const [moved] = next.splice(idx, 1);
    next.splice(to, 0, moved);
    await persistElevationOrder(next);
  }, [elevationOrder, folderStructure, persistElevationOrder, selectedFolder]);

  const moveElevationTo = useCallback(async (position) => {
    const keys = elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure || {});
    if (!selectedFolder || keys.length <= 1) return;
    const idx = keys.indexOf(selectedFolder);
    if (idx < 0) return;
    const next = [...keys];
    const [moved] = next.splice(idx, 1);
    if (position === 'first') next.unshift(moved);
    else next.push(moved);
    await persistElevationOrder(next);
  }, [elevationOrder, folderStructure, persistElevationOrder, selectedFolder]);

  // Auto Sort Elevations (Tabs Order)
  const handleAutoSort = async () => {
    const keys = Object.keys(folderStructure || {});
    if (keys.length === 0) return;

    const sortedKeys = [...keys].sort((a, b) => {
      // Extract numbers from folder keys if they start with digits
      const aMatch = a.match(/^(\d+)/);
      const bMatch = b.match(/^(\d+)/);
      
      if (aMatch && bMatch) {
        const numA = parseInt(aMatch[1], 10);
        const numB = parseInt(bMatch[1], 10);
        if (numA !== numB) {
          return numA - numB;
        }
        return a.localeCompare(b);
      }

      // Prioritize numeric folders over non-numeric ones
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      
      // Check for E1, E2 format
      const aMatchE = a.match(/E(\d+)/);
      const bMatchE = b.match(/E(\d+)/);
      if (aMatchE && bMatchE) {
        return parseInt(aMatchE[1], 10) - parseInt(bMatchE[1], 10);
      }
      
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
    await persistElevationOrder(sortedKeys);
  };

  const dragIndexRef = useRef(null);
  const overIndexRef = useRef(null);

  const [sitePlanOptions, setSitePlanOptions] = useState([]);
  const [elevationSitePlanMap, setElevationSitePlanMap] = useState({});
  const [sitePlanCandidates, setSitePlanCandidates] = useState([]);
  const [sitePlanImageUrl, setSitePlanImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [imageSortMode, setImageSortMode] = useState('manual');

  const sortImagesByMode = useCallback((images, mode) => {
    if (!Array.isArray(images) || mode === 'manual') return Array.isArray(images) ? images : [];

    const normalize = (v) => String(v || '').trim().toLowerCase();
    const getFilename = (img) => normalize(img?.filename || img?.name || img?.url || img?.id);
    const getCreatedAt = (img) => {
      const raw = img?.created_at || img?.createdAt || img?.uploaded_at || img?.uploadedAt;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    const sorted = [...images];
    sorted.sort((a, b) => {
      if (mode === 'filename_asc') return getFilename(a).localeCompare(getFilename(b));
      if (mode === 'filename_desc') return getFilename(b).localeCompare(getFilename(a));
      if (mode === 'created_desc') return getCreatedAt(b) - getCreatedAt(a);
      if (mode === 'created_asc') return getCreatedAt(a) - getCreatedAt(b);
      return 0;
    });
    return sorted;
  }, []);

  const currentFolderImages = selectedFolder ? (folderStructure[selectedFolder] || []) : [];
  const selectedCount = selectedImageIds.length;
  const allCurrentSelected = currentFolderImages.length > 0 && currentFolderImages.every((img) => selectedImageIds.includes(String(img.id)));
  const canManualReorder = imageSortMode === 'manual';
  const displayedImages = useMemo(() => {
    const images = selectedFolder ? (folderStructure[selectedFolder] || []) : [];
    return sortImagesByMode(images, imageSortMode);
  }, [folderStructure, selectedFolder, imageSortMode, sortImagesByMode]);

  const handleImageSortModeChange = useCallback(async (mode) => {
    setImageSortMode(mode);
    if (mode === 'manual' || previewOnly || !selectedFolder) return;

    const currentImages = folderStructure[selectedFolder] || [];
    const sortedImages = sortImagesByMode(currentImages, mode);
    const orderChanged = sortedImages.some((img, index) => img !== currentImages[index]);
    if (!orderChanged) return;

    const updated = {
      ...folderStructure,
      [selectedFolder]: sortedImages,
    };
    onUpdateFolderStructure && onUpdateFolderStructure(updated);

    try {
      const imageOrders = sortedImages
        .filter((img) => img?.id != null && img?.id !== '')
        .map((img, idx) => ({ id: String(img.id), order_position: idx + 1 }));
      if (imageOrders.length > 0) {
        await apiService.reorderImages(imageOrders);
      }
      setSuccess(`Images sorted by ${mode.replace('_', ' ')}`);
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e?.message || 'Failed to save image order');
    }
  }, [folderStructure, onUpdateFolderStructure, previewOnly, selectedFolder, sortImagesByMode]);

  const fetchSitePlans = useCallback(async () => {
    if (!buildingId) {
      setSitePlanCandidates([]);
      setSitePlanOptions([]);
      setSitePlanImageUrl('');
      return;
    }
    try {
      const plans = await apiService.getBuildingPlans(buildingId);
      const relevantPlans = (plans || []).filter(isSection12SitePlan);
      const urls = [];
      const options = [];
      relevantPlans.forEach((plan) => {
        if (plan.file_url && !String(plan.file_url).match(/\.pdf$/i)) {
          const absoluteUrl = apiService.baseURL
            ? `${apiService.baseURL}${plan.file_url}`
            : plan.file_url;
          urls.push(absoluteUrl);
          options.push({
            name: plan.name || `Plan ${options.length + 1}`,
            url: absoluteUrl
          });
        }
      });
      setSitePlanCandidates(urls);
      setSitePlanOptions(options);
      
      try {
        const stored = localStorage.getItem(`site_plan_${buildingId}`);
        if (stored && urls.includes(stored)) {
          setSitePlanImageUrl(stored);
        } else if (urls.length > 0) {
          setSitePlanImageUrl(urls[0]);
        } else {
          setSitePlanImageUrl('');
        }
      } catch {
        if (urls.length > 0) {
          setSitePlanImageUrl(urls[0]);
        } else {
          setSitePlanImageUrl('');
        }
      }
    } catch {
      setSitePlanCandidates([]);
      setSitePlanOptions([]);
    }
  }, [buildingId, apiService.baseURL]);

  useEffect(() => {
    fetchSitePlans();
  }, [fetchSitePlans]);

  useEffect(() => {
    setSelectedImageIds([]);
    setIsBulkDeleteMode(false);
  }, [selectedFolder]);

  const toggleImageSelection = useCallback((imageId) => {
    const id = String(imageId);
    setSelectedImageIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const handleSelectAllInFolder = useCallback(() => {
    if (!selectedFolder) return;
    const folderIds = (folderStructure[selectedFolder] || []).map((img) => String(img.id));
    setSelectedImageIds((prev) => {
      const allSelected = folderIds.length > 0 && folderIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !folderIds.includes(id));
      }
      const next = new Set(prev);
      folderIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [folderStructure, selectedFolder]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedFolder || selectedImageIds.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedImageIds.length} selected image(s)? This action cannot be undone.`);
    if (!confirmed) return;
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all(selectedImageIds.map((id) => apiService.deleteImage(id)));
      const updated = { ...folderStructure };
      updated[selectedFolder] = (updated[selectedFolder] || []).filter((img) => !selectedImageIds.includes(String(img.id)));
      onUpdateFolderStructure && onUpdateFolderStructure(updated);
      setSelectedImageIds([]);
      setIsBulkDeleteMode(false);
      setSuccess(`${selectedImageIds.length} image(s) deleted successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Bulk delete failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [folderStructure, onUpdateFolderStructure, selectedFolder, selectedImageIds]);

  const handleDeleteElevation = useCallback(async () => {
    if (!selectedFolder) return;
    const folderImages = folderStructure[selectedFolder] || [];
    const confirmed = window.confirm(
      `Delete elevation "${selectedFolder}" and its ${folderImages.length} image(s)? This action cannot be undone.`
    );
    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const storageElevationName = resolveElevationStorageName(selectedFolder);
      await apiService.deleteElevation(storageElevationName, projectId, buildingId, imageType);

      const updated = { ...folderStructure };
      delete updated[selectedFolder];
      onUpdateFolderStructure && onUpdateFolderStructure(updated);

      const remainingFolders = (elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure || {}))
        .filter((name) => name !== selectedFolder);
      const nextSelected = remainingFolders[0] || null;
      setSelectedFolder(nextSelected);

      const currentOrder = elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure || {});
      const nextOrder = currentOrder.filter((name) => name !== selectedFolder);
      setElevationOrder(nextOrder);
      if (buildingId) {
        try {
          await apiService.saveElevationOrder(buildingId, nextOrder, imageType);
        } catch (e) {
          console.error('Failed to update elevation order after delete', e);
        }
      }

      setElevationCoverMap((prev) => {
        const next = { ...(prev || {}) };
        delete next[selectedFolder];
        return next;
      });
      setElevationSitePlanMap((prev) => {
        const next = { ...(prev || {}) };
        delete next[selectedFolder];
        return next;
      });
      setSelectedImageIds([]);
      setIsBulkDeleteMode(false);
      setAnnotationModalOpen(false);
      setSelectedImageForAnnotation(null);

      const folderIdSet = new Set(folderImages.map((img) => String(img.id)));
      setKeyFindingsMap((prev) => {
        const next = (prev || []).filter((id) => !folderIdSet.has(String(id)));
        try {
          localStorage.setItem(`key_findings_${imageType}_building_${buildingId || 'global'}`, JSON.stringify(next));
        } catch {}
        return next;
      });

      setSuccess(`Elevation "${selectedFolder}" deleted successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Elevation delete failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [buildingId, elevationOrder, folderStructure, imageType, onUpdateFolderStructure, projectId, resolveElevationStorageName, selectedFolder]);

  const handleBulkDeleteAction = useCallback(() => {
    if (!isBulkDeleteMode) {
      setSelectedImageIds([]);
      setIsBulkDeleteMode(true);
      return;
    }
    handleBulkDelete();
  }, [handleBulkDelete, isBulkDeleteMode]);

  const handleCancelBulkDeleteMode = useCallback(() => {
    setIsBulkDeleteMode(false);
    setSelectedImageIds([]);
  }, []);

  const folderKeySignature = useMemo(() => {
    const keys = Object.keys(folderStructure || {});
    if (keys.length === 0) return '';
    return keys.slice().sort((a, b) => String(a).localeCompare(String(b))).join('|');
  }, [folderStructure]);

  const uploadSummary = useMemo(() => summarizeUploadSession(uploadSession), [uploadSession]);
  const uploadStatusByElevation = uploadSummary?.byElevation || {};
  const uploadButtonLabel = uploadSession?.status === 'local'
    ? 'Upload to server'
    : uploadSession?.status === 'paused'
      ? 'Resume upload'
      : 'Upload to server';

  // Load elevation site plan map
  useEffect(() => {
    if (!buildingId) return;
    const loadMap = async () => {
      try {
        const result = await apiService.getSitePlanAnnotations(buildingId);
        if (result && result.elevation_map) {
          setElevationSitePlanMap(result.elevation_map);
        }
      } catch (e) {
        console.error('Error loading elevation map:', e);
      }
    };
    loadMap();
  }, [buildingId]);

  useEffect(() => {
    const keys = Object.keys(folderStructure || {});
    if (keys.length === 0) {
      setElevationOrder([]);
      return;
    }
    if (buildingId) {
      apiService.getElevationOrder(buildingId, imageType)
        .then((resp) => {
          const persisted = resp && Array.isArray(resp.order) ? resp.order : [];
          if (persisted.length > 0) {
            const filtered = persisted.filter((k) => keys.includes(k));
            const extras = keys.filter((k) => !filtered.includes(k));
            setElevationOrder([...filtered, ...extras]);
          } else {
            setElevationOrder(keys);
          }
        })
        .catch(() => {
          setElevationOrder(keys);
        });
      apiService.getElevationCover(buildingId, imageType)
        .then((resp) => {
          const mp = resp && resp.cover_by_elevation ? resp.cover_by_elevation : {};
          setElevationCoverMap(mp || {});
        })
        .catch(() => setElevationCoverMap({}));
      let cancelled = false;
      apiService.listKeyFindings(buildingId, imageType)
        .then((resp) => {
          if (cancelled) return;
          const ids = (resp?.items || []).map((item) => String(item.image_id));
          setKeyFindingsMap(ids);
          try {
            localStorage.setItem(`key_findings_${imageType}_building_${buildingId}`, JSON.stringify(ids));
          } catch {}
        })
        .catch(() => {
          if (cancelled) return;
          try {
            const raw = localStorage.getItem(`key_findings_${imageType}_building_${buildingId}`);
            const arr = raw ? JSON.parse(raw) : [];
            setKeyFindingsMap(Array.isArray(arr) ? arr.map(String) : []);
          } catch {
            setKeyFindingsMap([]);
          }
        });
      return () => {
        cancelled = true;
      };
    } else {
      setElevationOrder(keys);
    }
  }, [folderKeySignature, buildingId, imageType]);

  useEffect(() => {
    const names = elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure || {});
    if (names.length > 0 && !names.includes(selectedFolder)) {
      setSelectedFolder(names[0]);
    }
  }, [elevationOrder, folderStructure, selectedFolder, setSelectedFolder]);

  const handleLinkElevationPlan = async (elevationName, url) => {
    const newMap = { ...elevationSitePlanMap, [elevationName]: url };
    setElevationSitePlanMap(newMap);
    
    try {
      // Need to fetch current annotations first to avoid overwriting by_plan_url
      // Note: In a real concurrent environment this is race-prone, but sufficient for this dashboard
      const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
      const payload = {
        ...current,
        elevation_map: newMap
      };
      await apiService.saveSitePlanAnnotations(buildingId, payload);
    } catch (e) {
      console.error('Error saving elevation map:', e);
    }
  };

  const handleSitePlanFileChange = async (e, elevationName) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      
      const file = files[0];
      try {
          setIsLoading(true);
          const plan = await apiService.uploadBuildingPlan(
              buildingId,
              file,
              `Site Plan - ${file.name}`,
              `Uploaded from elevation ${elevationName}`
          );
          await fetchSitePlans();
          
          if (plan && plan.file_url) {
            const absoluteUrl = apiService.baseURL && !plan.file_url.startsWith('http')
              ? `${apiService.baseURL}${plan.file_url}`
              : plan.file_url;
            handleLinkElevationPlan(elevationName, absoluteUrl);
          }
          
          setSuccess('Site plan uploaded successfully');
          setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
          console.error('Error uploading site plan:', err);
          setError('Failed to upload site plan');
      } finally {
          setIsLoading(false);
          e.target.value = '';
      }
  };

  const folderNames = elevationOrder.length > 0 ? elevationOrder : Object.keys(folderStructure);

  const getAllImages = useCallback(() => {
    const allImages = [];
    (folderNames || []).forEach((elevationName) => {
      const images = folderStructure[elevationName] || [];
      images.forEach((image, index) => {
        allImages.push({
          ...image,
          elevationName,
          index,
        });
      });
    });
    return allImages;
  }, [folderNames, folderStructure]);

  const handleNavigateImage = useCallback((newIndex) => {
    const allImages = getAllImages();
    if (newIndex >= 0 && newIndex < allImages.length) {
      setCurrentImageIndex(newIndex);
      const nextImage = allImages[newIndex];
      const floorName =
        (nextImage && (nextImage.floor_name || nextImage.floorName)) ||
        (nextImage && typeof nextImage.floor === 'string' ? nextImage.floor : '') ||
        (nextImage && nextImage.originalImage && (nextImage.originalImage.floor_name || nextImage.originalImage.floorName || nextImage.originalImage.floor)) ||
        '';
      setSelectedImageForAnnotation({
        ...nextImage,
        floor_name: floorName,
        floorName: floorName,
      });
    }
  }, [getAllImages]);

  const handleEditImage = useCallback((image, elevationName) => {
    const allImages = getAllImages();
    const imageWithElevation = { ...image, elevationName, floor_name: image?.floor_name || image?.floorName || image?.floor || '' };
    const imageIndex = allImages.findIndex((img) => img.id === image.id && img.elevationName === elevationName);
    setSelectedImageForAnnotation(imageWithElevation);
    setCurrentImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setAnnotationModalOpen(true);
  }, [getAllImages]);

  useEffect(() => {
    const handleLevelUpdated = (evt) => {
      const imageId = evt?.detail?.imageId;
      const floorName = evt?.detail?.floor_name;
      if (!imageId) return;
      const nextFloor = String(floorName || '').trim();
      if (!nextFloor) return;

      const updated = { ...folderStructure };
      let changed = false;
      Object.keys(updated).forEach((elev) => {
        const arr = Array.isArray(updated[elev]) ? updated[elev] : [];
        const nextArr = arr.map((img) => {
          if (String(img?.id) !== String(imageId)) return img;
          changed = true;
          return {
            ...img,
            floor_name: nextFloor,
            floorName: nextFloor,
            floor: nextFloor,
          };
        });
        updated[elev] = nextArr;
      });
      if (changed) {
        onUpdateFolderStructure && onUpdateFolderStructure(updated);
        setSelectedImageForAnnotation((prev) => {
          if (!prev) return prev;
          if (String(prev?.id) !== String(imageId)) return prev;
          return { ...prev, floor_name: nextFloor, floorName: nextFloor, floor: nextFloor };
        });
      }
    };
    window.addEventListener('pfi-image-level-updated', handleLevelUpdated);
    return () => window.removeEventListener('pfi-image-level-updated', handleLevelUpdated);
  }, [folderStructure, onUpdateFolderStructure]);

  useEffect(() => {
    if (selectedImageForAnnotation && elevationSitePlanMap && sitePlanCandidates.length > 0) {
      const elevName = selectedImageForAnnotation.elevationName;
      const linkedUrl = elevationSitePlanMap[elevName];
      if (linkedUrl && sitePlanCandidates.includes(linkedUrl)) {
        setSitePlanImageUrl(linkedUrl);
      }
    }
  }, [selectedImageForAnnotation, elevationSitePlanMap, sitePlanCandidates]);

  const handleChangeSitePlan = (url) => {
    const next = url || '';
    setSitePlanImageUrl(next);
    try {
      if (buildingId) {
        if (next) {
          localStorage.setItem(`site_plan_${buildingId}`, next);
        } else {
          localStorage.removeItem(`site_plan_${buildingId}`);
        }
      }
    } catch {}
  };

  // Helper to format folder label
  const getFolderLabel = (name) => {
    // If we have a building name and the folder is just a number or code, append the name
    // e.g. "01" -> "01 - Building Name"
    if (buildingName) {
        // Check if name is simple (digits or short code like E1)
        if (name.match(/^(\d+|E\d+)$/)) {
            return `${name}`;
        }
    }
    return name; 
  };

  return (
    <Box sx={{ width: '100%' }}>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {uploadSession && uploadSummary?.resumable && (
        <Alert
          severity={uploadSession.status === 'paused' ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={onResumeUpload ? (
            <Button color="inherit" size="small" onClick={onResumeUpload} disabled={uploadSession.status === 'uploading'}>
              {uploadSession.status === 'uploading' ? 'Uploading...' : uploadButtonLabel}
            </Button>
          ) : null}
        >
          {uploadSession.status === 'uploading'
            ? 'Uploading the cached files to the server right now.'
            : uploadSession.status === 'paused'
            ? 'The current upload paused, but the files are still cached locally.'
            : uploadSession.status === 'local'
              ? 'This is a local preview only. Review the elevations, then upload them to the server.'
              : 'A cached upload session is active for these elevations.'}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1, gap: 1, flexWrap: 'wrap' }}>
        {!previewOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Elevation Order:
            </Typography>
            <Tooltip title="Move elevation to first">
              <span>
                <IconButton size="small" onClick={() => moveElevationTo('first')} disabled={!selectedFolder || folderNames.length <= 1}>
                  <VerticalAlignTopIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move elevation left">
              <span>
                <IconButton size="small" onClick={() => moveElevationBy(-1)} disabled={!selectedFolder || folderNames.length <= 1}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move elevation right">
              <span>
                <IconButton size="small" onClick={() => moveElevationBy(1)} disabled={!selectedFolder || folderNames.length <= 1}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move elevation to last">
              <span>
                <IconButton size="small" onClick={() => moveElevationTo('last')} disabled={!selectedFolder || folderNames.length <= 1}>
                  <VerticalAlignBottomIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
          {!previewOnly && (
            <Tooltip title="Auto-sort folders numerically/alphabetically">
              <Button 
                variant="text" 
                size="small" 
                startIcon={<SortIcon />} 
                onClick={handleAutoSort}
              >
                Auto Sort
              </Button>
            </Tooltip>
          )}
        </Box>
      {folderNames.length > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedFolder}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="folder tabs"
            >
              {folderNames.map((folderName) => (
                <FolderTab
                  key={folderName}
                  value={folderName}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4, minWidth: 140 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {getFolderLabel(folderName)}
                      </Typography>
                      {uploadStatusByElevation[folderName] && uploadSession?.status === 'local' && (
                        <Typography variant="caption" color="text.secondary">
                          Local preview
                        </Typography>
                      )}
                      {uploadStatusByElevation[folderName] && uploadSession?.status !== 'local' && (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={uploadStatusByElevation[folderName].percent || 0}
                            sx={{ width: '100%', height: 6, borderRadius: 999 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {uploadStatusByElevation[folderName].completed}/{uploadStatusByElevation[folderName].total} uploaded
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          
          {selectedFolder && (
            <FolderContainer>
              <FolderHeader sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                {!isRenaming ? (
                  <FolderName variant="h6">{getFolderLabel(selectedFolder)}</FolderName>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      size="small"
                      autoFocus
                      placeholder="New folder name"
                    />
                    <Button variant="contained" size="small" onClick={applyRename}>Save</Button>
                    <Button variant="text" size="small" onClick={cancelRename}>Cancel</Button>
                  </Stack>
                )}
                {!isRenaming && !previewOnly && (
                    <Tooltip title="Rename this folder">
                      <IconButton size="small" onClick={beginRename} sx={{ mr: 1 }}>
                        <DriveFileRenameOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {folderStructure[selectedFolder]?.length || 0} images
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {!previewOnly && (
                    <>
                      <Tooltip title={canManualReorder ? 'Sorting images (manual drag order)' : 'Sorting images (drag reorder disabled)'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                          <SortIcon fontSize="small" />
                          <FormControl size="small" sx={{ minWidth: 170 }}>
                            <Select
                              value={imageSortMode}
                              onChange={(e) => handleImageSortModeChange(e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="manual">Manual (Drag)</MenuItem>
                              <MenuItem value="filename_asc">Filename A-Z</MenuItem>
                              <MenuItem value="filename_desc">Filename Z-A</MenuItem>
                              <MenuItem value="created_desc">Newest</MenuItem>
                              <MenuItem value="created_asc">Oldest</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Tooltip>
                      {sitePlanOptions.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                            Link to Site Plan:
                          </Typography>
                          <FormControl size="small" variant="standard" sx={{ minWidth: 150, mr: 1 }}>
                            <Select
                              value={elevationSitePlanMap[selectedFolder] || ''}
                              onChange={(e) => handleLinkElevationPlan(selectedFolder, e.target.value)}
                              displayEmpty
                              disableUnderline
                              sx={{ fontSize: '0.875rem' }}
                            >
                              <MenuItem value="">
                                <em style={{ color: '#aaa' }}>None</em>
                              </MenuItem>
                              {sitePlanOptions.map((opt) => (
                                <MenuItem key={opt.url} value={opt.url}>
                                  {opt.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      )}
                      <Tooltip title="Upload and set a new site plan for this elevation">
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFileIcon />}
                          sx={{ mr: 1, whiteSpace: 'nowrap' }}
                        >
                          Set Site Plan
                          <VisuallyHiddenInput 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => handleSitePlanFileChange(e, selectedFolder)} 
                          />
                        </Button>
                      </Tooltip>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.multiple = true;
                          input.onchange = async (e) => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;
                            try {
                              const storageElevationName = resolveElevationStorageName(selectedFolder);
                              const result = await apiService.uploadImagesToElevation(files, storageElevationName, projectId, buildingId, imageType);
                              // Merge new images into structure
                              const updated = { ...folderStructure };
                              if (!updated[selectedFolder]) updated[selectedFolder] = [];
                              const added = [];
                              if (result.folder_structure && result.folder_structure[storageElevationName]) {
                                const imgs = result.folder_structure[storageElevationName];
                                imgs.forEach((backendImage, index) => {
                                  added.push({
                                    id: backendImage.id,
                                    src: apiService.getImageUrl(backendImage.id),
                                    elevation: selectedFolder,
                                    floor: backendImage.floor_name || `Floor ${index + 1}`,
                                    filename: backendImage.filename,
                                    url: backendImage.url,
                                    folderPath: selectedFolder,
                                    folderName: selectedFolder,
                                    sourceElevationName: storageElevationName,
                                    originalElevationName: storageElevationName,
                                    uploadStatus: 'server',
                                    uploadSource: 'server',
                                  });
                                });
                              } else if (Array.isArray(result.images)) {
                                result.images.forEach((backendImage, index) => {
                                  added.push({
                                    id: backendImage.id,
                                    src: apiService.getImageUrl(backendImage.id),
                                    elevation: selectedFolder,
                                    floor: backendImage.floor_name || `Floor ${index + 1}`,
                                    filename: backendImage.filename,
                                    url: backendImage.url,
                                    folderPath: selectedFolder,
                                    folderName: selectedFolder,
                                    sourceElevationName: storageElevationName,
                                    originalElevationName: storageElevationName,
                                    uploadStatus: 'server',
                                    uploadSource: 'server',
                                  });
                                });
                              }
                              updated[selectedFolder] = [...updated[selectedFolder], ...added];
                              onUpdateFolderStructure && onUpdateFolderStructure(updated);
                            } catch (err) {
                              alert(`Upload failed: ${err.message}`);
                            }
                          };
                          input.click();
                        }}
                      >
                        Upload to this folder
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        disabled={!selectedFolder || isLoading}
                        onClick={handleDeleteElevation}
                      >
                        Delete Elevation
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        disabled={(isBulkDeleteMode && selectedCount === 0) || isLoading}
                        onClick={handleBulkDeleteAction}
                      >
                        {isBulkDeleteMode ? `Delete Selected${selectedCount > 0 ? ` (${selectedCount})` : ''}` : 'Bulk Delete'}
                      </Button>
                      {isBulkDeleteMode && (
                        <>
                          <Button
                            variant={allCurrentSelected ? 'contained' : 'outlined'}
                            size="small"
                            onClick={handleSelectAllInFolder}
                          >
                            {allCurrentSelected ? 'Clear Selection' : 'Select All'}
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleCancelBulkDeleteMode}
                          >
                            Cancel Selection
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Box>
              </FolderHeader>
              
              <Divider />
              
              <ImageGrid>
                {displayedImages.map((image, index) => (
                  <SelectableImageCard key={image.id} selected={selectedImageIds.includes(String(image.id))}>
                    {isBulkDeleteMode && (
                      <Checkbox
                        checked={selectedImageIds.includes(String(image.id))}
                        onChange={() => toggleImageSelection(image.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 3,
                          backgroundColor: 'rgba(255,255,255,0.92)',
                          borderRadius: '50%',
                          p: 0.5
                        }}
                      />
                    )}
                    <ImagePreview
                      image={image}
                      buildingId={buildingId}
                      elevationName={selectedFolder}
                      imageType={imageType}
                      onClick={previewOnly ? null : ((img) => {
                        if (isBulkDeleteMode) {
                          toggleImageSelection(img.id);
                          return;
                        }
                        handleEditImage(img, selectedFolder);
                      })}
                      isCover={Boolean(
                        (elevationCoverMap[selectedFolder] || elevationCoverMap[resolveElevationStorageName(selectedFolder)]) &&
                        String(elevationCoverMap[selectedFolder] || elevationCoverMap[resolveElevationStorageName(selectedFolder)]) === String(image.id)
                      )}
                      isKeyFinding={keyFindingsMap.includes(String(image.id))}
                      onToggleKeyFinding={previewOnly ? null : (async (img) => {
                        const id = String(img.id);
                        const alreadySelected = keyFindingsMap.includes(id);
                        if (!alreadySelected) {
                          const hasKnownDefects =
                            hasMeaningfulDefects(img?.defects) ||
                            hasMeaningfulDefects(img?.originalImage?.defects);
                          let hasDefects = hasKnownDefects;
                          if (!hasDefects && img?.id) {
                            try {
                              const resp = await apiService.getImageDefects(img.id);
                              hasDefects = hasMeaningfulDefects(resp?.defects);
                            } catch (e) {
                              console.error('Failed to check defects before marking key finding', e);
                            }
                          }
                          if (!hasDefects) {
                            try {
                              const idPart =
                                img?.id ||
                                img?.file_url ||
                                img?.url ||
                                img?.src ||
                                img?.filename ||
                                (img?.originalImage && (img.originalImage.id || img.originalImage.url || img.originalImage.src));
                              const bPart = buildingId || img?.building_id || '';
                              const raw = idPart ? localStorage.getItem(`annotations_${String(idPart)}_${String(bPart)}_data`) : null;
                              const data = raw ? JSON.parse(raw) : null;
                              hasDefects = hasMeaningfulDefects(data?.defects);
                            } catch {}
                          }
                          if (!hasDefects) {
                            setError('Only images with defects can be marked as key findings.');
                            return;
                          }
                        }
                        if (buildingId && img?.id) {
                          try {
                            if (alreadySelected) {
                              await apiService.removeKeyFinding(buildingId, img.id, imageType);
                            } else {
                              await apiService.addKeyFinding(buildingId, img.id, imageType);
                            }
                          } catch (e) {
                            console.error('Failed to update key finding', e);
                            setError(e?.message || 'Failed to update key finding');
                            return;
                          }
                        }
                        setKeyFindingsMap((prev) => {
                          const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
                          try {
                            localStorage.setItem(`key_findings_${imageType}_building_${buildingId}`, JSON.stringify(next));
                          } catch {}
                          return next;
                        });
                      })}
                      onSetCover={previewOnly ? null : (async (img) => {
                        try {
                          if (!buildingId || !img?.id) return;
                          const storageElevationName = resolveElevationStorageName(selectedFolder);
                          await apiService.setElevationCover(buildingId, storageElevationName, img.id, imageType);
                          setElevationCoverMap((prev) => ({
                            ...(prev || {}),
                            [selectedFolder]: img.id,
                            [storageElevationName]: img.id,
                          }));
                        } catch (e) {
                          console.error('Failed to set elevation cover', e);
                        }
                      })}
                      isDraggable={!previewOnly && !isBulkDeleteMode && canManualReorder}
                      onDragStart={(e) => {
                        if (previewOnly || isBulkDeleteMode || !canManualReorder) return;
                        dragIndexRef.current = index;
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragOver={(e) => {
                        if (previewOnly || isBulkDeleteMode || !canManualReorder) return;
                        e.preventDefault();
                        overIndexRef.current = index;
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={async (e) => {
                        if (previewOnly || isBulkDeleteMode || !canManualReorder) return;
                        e.preventDefault();
                        const from = dragIndexRef.current;
                        const to = overIndexRef.current;
                        if (from == null || to == null || from === to) return;
                        const updated = { ...folderStructure };
                        const arr = [...(updated[selectedFolder] || [])];
                        const [moved] = arr.splice(from, 1);
                        arr.splice(to, 0, moved);
                        updated[selectedFolder] = arr;
                        onUpdateFolderStructure && onUpdateFolderStructure(updated);

                        try {
                          const imageOrders = arr
                            .filter(img => img.id && typeof img.id === 'string')
                            .map((img, idx) => ({ id: img.id, order_position: idx + 1 }));
                          if (imageOrders.length > 0) {
                            await apiService.reorderImages(imageOrders);
                          }
                          if (buildingId) {
                            try {
                              const allImagesOrdered = [];
                              (folderNames || []).forEach((elevationName) => {
                                const imgs = updated[elevationName] || [];
                                imgs.forEach((img) => {
                                  allImagesOrdered.push({ ...img, elevationName });
                                });
                              });
                              const photoNoById = new Map();
                              let imageNumberOffset = 0;
                              if (String(imageType || 'drone') === 'tapping') {
                                try {
                                  const droneResp = await apiService.getBuildingImages(buildingId, 'drone');
                                  const droneFolders = droneResp?.folder_structure || {};
                                  imageNumberOffset = Object.values(droneFolders).reduce(
                                    (total, list) => total + (Array.isArray(list) ? list.length : 0),
                                    0
                                  );
                                } catch (e3) {
                                  console.error('Failed to load drone image count for tapping renumber', e3);
                                }
                              }
                              allImagesOrdered.forEach((img, idx2) => {
                                if (img?.id) photoNoById.set(String(img.id), imageNumberOffset + idx2 + 1);
                              });
                              const uniqueByLevel = new Map();
                              const seenLevel = new Set();
                              allImagesOrdered.forEach((img) => {
                                const level = String(img?.floor_name || img?.floorName || img?.floor || '').trim();
                                const key = `${imageType || 'drone'}|${level}`;
                                if (!level) return;
                                if (seenLevel.has(key)) {
                                  uniqueByLevel.delete(key);
                                } else {
                                  uniqueByLevel.set(key, String(img.id));
                                  seenLevel.add(key);
                                }
                              });

                              const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
                              const byPlan = current?.by_plan_url || {};
                              const nextByPlan = {};
                              Object.entries(byPlan).forEach(([url, anns]) => {
                                if (!url || !Array.isArray(anns)) return;
                                const next = [];
                                anns.forEach((ann) => {
                                  const context = ann?.context || 'drone';
                                  let imgId = ann?.image_id != null ? String(ann.image_id) : null;
                                  if (!imgId && typeof ann?.id === 'string' && photoNoById.has(String(ann.id))) {
                                    imgId = String(ann.id);
                                  }
                                  if (!imgId && ann?.level) {
                                    const k = `${context}|${String(ann.level).trim()}`;
                                    const candidate = uniqueByLevel.get(k);
                                    if (candidate) imgId = candidate;
                                  }
                                  if (imgId && photoNoById.has(imgId)) {
                                    next.push({
                                      ...ann,
                                      id: imgId,
                                      image_id: imgId,
                                      label: String(photoNoById.get(imgId)),
                                    });
                                  } else {
                                    next.push(ann);
                                  }
                                });
                                const dedup = new Map();
                                next.forEach((ann) => {
                                  const key = `${String(ann?.image_id || ann?.id || ann?.label || '')}|${ann?.context || 'drone'}`;
                                  dedup.set(key, ann);
                                });
                                nextByPlan[url] = Array.from(dedup.values());
                              });
                              await apiService.saveSitePlanAnnotations(buildingId, {
                                ...current,
                                by_plan_url: nextByPlan,
                              });
                            } catch (e2) {
                              console.error('Failed to renumber site plan annotations', e2);
                            }
                          }
                        } catch (err) {
                          console.error('Reorder failed', err);
                        } finally {
                          dragIndexRef.current = null;
                          overIndexRef.current = null;
                        }
                      }}
                      onDragEnd={() => {
                        dragIndexRef.current = null;
                        overIndexRef.current = null;
                      }}
                      onDelete={previewOnly ? null : (async (img) => {
                        try {
                          await apiService.deleteImage(img.id);
                          const updated = { ...folderStructure };
                          updated[selectedFolder] = (updated[selectedFolder] || []).filter(i => i.id !== img.id);
                          onUpdateFolderStructure && onUpdateFolderStructure(updated);
                          setSelectedImageIds((prev) => prev.filter((id) => id !== String(img.id)));
                        } catch (err) {
                          alert(`Delete failed: ${err.message}`);
                        }
                      })}
                    />
                  </SelectableImageCard>
                ))}
              </ImageGrid>
            </FolderContainer>
          )}
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          No folders uploaded yet. Use the "Upload Folder" button to upload a folder with images.
        </Typography>
      )}
      <ImageAnnotationModal
        open={annotationModalOpen}
        onClose={() => {
          setAnnotationModalOpen(false);
          setSelectedImageForAnnotation(null);
        }}
        image={selectedImageForAnnotation}
        images={getAllImages()}
        currentImageIndex={currentImageIndex}
        onNavigateImage={handleNavigateImage}
        sitePlanImageUrl={sitePlanImageUrl}
        sitePlanCandidates={sitePlanCandidates}
        onChangeSitePlan={handleChangeSitePlan}
        buildingId={buildingId}
        imageType={imageType}
        showSitePlanPanel={sitePlanCandidates.length > 0}
        coverAnnotationMode={Boolean(
          selectedImageForAnnotation &&
          (elevationCoverMap[selectedImageForAnnotation.elevationName] ||
            elevationCoverMap[resolveElevationStorageName(selectedImageForAnnotation.elevationName)]) &&
          String(elevationCoverMap[selectedImageForAnnotation.elevationName] ||
            elevationCoverMap[resolveElevationStorageName(selectedImageForAnnotation.elevationName)]) === String(selectedImageForAnnotation.id)
        )}
        onSave={(annotationData) => {
          if (selectedImageForAnnotation) {
            const hasAnnotations = Array.isArray(annotationData?.annotations) && annotationData.annotations.length > 0;
            const updated = { ...folderStructure };
            updated[selectedImageForAnnotation.elevationName] = (updated[selectedImageForAnnotation.elevationName] || []).map((img) =>
              img.id === selectedImageForAnnotation.id
                ? { ...img, annotated: hasAnnotations, annotations: annotationData }
                : img
            );
            onUpdateFolderStructure && onUpdateFolderStructure(updated);
          }
          setSuccess('Annotations saved successfully');
          setTimeout(() => setSuccess(null), 3000);
        }}
      />
    </Box>
  );
};

export default FolderView;
